import { create } from 'zustand';
import { Proposal, ProposalCreate, ProposalUpdate } from '../types/proposal';
import api from '../config/axios';
import { PROPOSALS } from '../config/apiPaths';
import { notifyEntityCreated, notifyEntityUpdated } from '../utils/entityNotifications';

interface ProposalStore {
  proposals: Proposal[];
  currentProposal: Proposal | null;
  isLoading: boolean;
  error: string | null;

  fetchProposals: (organizationId?: number) => Promise<void>;
  fetchProposalById: (id: number) => Promise<void>;
  createProposal: (proposal: ProposalCreate) => Promise<void>;
  updateProposal: (id: number, proposal: ProposalUpdate) => Promise<void>;
  deleteProposal: (id: number) => Promise<void>;
  submitPublicProposal: (proposal: ProposalCreate) => Promise<void>;

  getProposalById: (id: number) => Proposal | undefined;
  clearError: () => void;
}

export const useProposalStore = create<ProposalStore>((set, get) => ({
  proposals: [],
  currentProposal: null,
  isLoading: false,
  error: null,

  fetchProposals: async (organizationId?: number) => {
    set({ isLoading: true, error: null });
    try {
      const params = organizationId ? { organization_id: organizationId } : {};
      const response = await api.get(PROPOSALS.ALL, { params });
      
      set({ proposals: response.data || [], isLoading: false });
    } catch (error: any) {
      console.error('Error fetching proposals:', error);
      const errorMessage = error.response?.data?.message || 'Failed to fetch proposals';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  fetchProposalById: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get(PROPOSALS.BY_ID(id));
      set({ currentProposal: response.data, isLoading: false });
    } catch (error: any) {
      console.error(`Error fetching proposal with ID ${id}:`, error);
      const errorMessage = error.response?.data?.message || 'Failed to fetch proposal';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  createProposal: async (proposal: ProposalCreate) => {
    set({ isLoading: true, error: null });
    try {
      // Convert string fields to numbers where needed
      const createPayload = { ...proposal };
      if ('contact_id' in createPayload && createPayload.contact_id !== undefined) {
        createPayload.contact_id = Number(createPayload.contact_id);
      }
      
      // Organization ID will be set by the backend handler from auth context
      
      const response = await api.post(PROPOSALS.ALL, createPayload);
      set(state => ({
        proposals: [...state.proposals, response.data],
        isLoading: false
      }));
      
      // Notify about the new proposal creation
      notifyEntityCreated('proposal', {
        id: response.data.id,
        title: response.data.title,
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error creating proposal:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create proposal';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  updateProposal: async (id: number, proposal: ProposalUpdate) => {
    set({ isLoading: true, error: null });
    try {
      // Convert string fields to numbers where needed
      const updatePayload = { ...proposal };
      if ('contact_id' in updatePayload && updatePayload.contact_id !== undefined) {
        updatePayload.contact_id = Number(updatePayload.contact_id);
      }
      
      const response = await api.put(PROPOSALS.BY_ID(id), updatePayload);
      set(state => ({
        proposals: state.proposals.map(p => p.id === id ? response.data : p),
        currentProposal: state.currentProposal?.id === id ? response.data : state.currentProposal,
        isLoading: false
      }));
      
      // Notify about the proposal update
      notifyEntityUpdated('proposal', {
        id: response.data.id,
        title: response.data.title,
      });
      
      return response.data;
    } catch (error: any) {
      console.error(`Error updating proposal with ID ${id}:`, error);
      const errorMessage = error.response?.data?.message || 'Failed to update proposal';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  deleteProposal: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(PROPOSALS.BY_ID(id));
      set(state => ({
        proposals: state.proposals.filter(p => p.id !== id),
        currentProposal: state.currentProposal?.id === id ? null : state.currentProposal,
        isLoading: false
      }));
    } catch (error: any) {
      console.error(`Error deleting proposal with ID ${id}:`, error);
      const errorMessage = error.response?.data?.message || 'Failed to delete proposal';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  submitPublicProposal: async (proposal: ProposalCreate) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post(PROPOSALS.PUBLIC, proposal);
      set({ isLoading: false });
      
      // Notify about the public proposal submission
      notifyEntityCreated('proposal', {
        id: response.data.id,
        title: response.data.title,
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error submitting public proposal:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit proposal';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  getProposalById: (id: number) => get().proposals.find(p => p.id === id),
  clearError: () => set({ error: null }),
}));